// Ported from 21st.dev "Feature Grid Spotlight Cards" (Hirael feature-08, MIT · Mohammad Shehadeh).
// TSX/shadcn tokens → JSX + AFS Tailwind palette; content comes in via props.
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import Icon from '@/components/site/Icon';

/** Corner crosshair, drawn half outside the card edge like a survey mark. */
function CrossDecor({ position }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      className={cn(
        'pointer-events-none absolute z-10 h-3.5 w-3.5 shrink-0 text-primary-300',
        position === 'top-start' && 'left-0 top-0 -translate-x-1/2 -translate-y-1/2',
        position === 'bottom-end' && 'bottom-0 right-0 translate-x-1/2 translate-y-1/2',
      )}
    >
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </svg>
  );
}

export function SpotlightCard({ className, children, ...props }) {
  const handlePointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    e.currentTarget.style.setProperty('--my', `${e.clientY - rect.top}px`);
  };

  return (
    <div
      onPointerMove={handlePointerMove}
      className={cn(
        'group relative flex h-full flex-col justify-start gap-5 bg-white px-6 pb-6 pt-8 shadow-sm',
        'bg-[radial-gradient(50%_80%_at_25%_0%,rgba(245,144,11,0.10),transparent)]',
        className,
      )}
      {...props}
    >
      {/* Pointer-follow spotlight, fades in on hover along --mx/--my. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 bg-[radial-gradient(circle_220px_at_var(--mx)_var(--my),rgba(229,62,65,0.12),transparent_70%)]"
      />
      <div className="absolute -inset-y-4 -left-px w-px bg-[var(--app-border)]" />
      <div className="absolute -inset-y-4 -right-px w-px bg-[var(--app-border)]" />
      <div className="absolute -inset-x-4 -top-px h-px bg-[var(--app-border)]" />
      <div className="absolute -inset-x-4 -bottom-px h-px bg-[var(--app-border)]" />
      <CrossDecor position="top-start" />
      <CrossDecor position="bottom-end" />
      {children}
    </div>
  );
}

/** Grid of icon + title + text cards. `items`: [{ icon, title, text, to? , image? }] */
export default function SpotlightCards({ items = [], columns = 3, className }) {
  return (
    <div
      className={cn(
        'grid w-full grid-cols-1 gap-8 md:grid-cols-2',
        columns === 3 && 'lg:grid-cols-3',
        columns === 4 && 'lg:grid-cols-4',
        className,
      )}
    >
      {items.map((item) => {
        const body = (
          <SpotlightCard className={item.to ? 'cursor-pointer' : ''}>
            {item.image && (
              <div className="relative z-10 -mx-6 -mt-8 mb-1 aspect-[16/10] overflow-hidden">
                <img src={item.image} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
            )}
            <div className="relative z-10 flex w-fit items-center justify-center rounded-lg border border-[var(--app-border)] bg-primary-50 p-3 text-primary-600 transition-colors duration-300 group-hover:border-primary-300 group-hover:bg-primary-600 group-hover:text-white">
              <Icon name={item.icon} className="h-5 w-5 stroke-[1.75]" />
            </div>
            <div className="relative z-10 flex flex-1 flex-col gap-2">
              <h3 className="text-xl font-bold leading-tight text-ink-900">{item.title}</h3>
              <p className="text-sm leading-relaxed text-ink-500">{item.text}</p>
            </div>
            {item.to && (
              <span className="relative z-10 inline-flex items-center gap-1 text-sm font-medium text-primary-600">
                Learn more <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
              </span>
            )}
          </SpotlightCard>
        );
        return item.to ? (
          <Link key={item.title} to={item.to} className="block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
            {body}
          </Link>
        ) : (
          <div key={item.title}>{body}</div>
        );
      })}
    </div>
  );
}
